import React, {
    useEffect,
    useMemo,
    useState
} from "react";

import * as XLSX from "xlsx";

import {
    Search,
    Mic,
    RefreshCw,
    Eye,
    Pencil,
    Printer,
    Trash2,
    Package,
    Weight,
    Boxes,
    MapPin,
    User,
    X,
    AlertTriangle,
    FileSpreadsheet
} from "lucide-react";

import {
    getInventory,
    deleteGRN
} from "../services/api";

import {
    useNavigate
} from "react-router-dom";

import "./Inventory.css";


const Inventory = () => {

    const navigate = useNavigate();


    // =====================================================
    // STATE
    // =====================================================

    const [inventory, setInventory] =
        useState([]);

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState("");

    const [search, setSearch] =
        useState("");

    const [registerListening, setRegisterListening] = useState(false);

    const startRegisterVoiceSearch = () => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            window.alert("Voice search requires an updated Chrome browser.");
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.lang = "mr-IN";
        recognition.continuous = false;
        recognition.interimResults = false;

        recognition.onresult = (event) => {
            const spokenText = event.results?.[0]?.[0]?.transcript || "";
            if (spokenText.trim()) setSearch(spokenText.trim());
        };
        recognition.onerror = (event) => {
            if (event.error === "not-allowed") {
                window.alert("Please allow microphone access in your browser.");
            } else if (event.error === "no-speech") {
                window.alert("No speech detected. Please try again.");
            }
        };
        recognition.onend = () => setRegisterListening(false);

        try {
            setRegisterListening(true);
            recognition.start();
        } catch (error) {
            setRegisterListening(false);
            console.error("Voice search failed:", error);
        }
    };


    const [statusFilter, setStatusFilter] =
        useState("ALL");

    const [chamberFilter, setChamberFilter] =
        useState("ALL");

    const [deleteItem, setDeleteItem] =
        useState(null);

    const [deleting, setDeleting] =
        useState(false);


    // =====================================================
    // LOAD INVENTORY
    // =====================================================

    const loadInventory = async () => {

        try {

            setLoading(true);

            setError("");

            const response =
                await getInventory();

            console.log(
                "Inventory API Response:",
                response
            );

            setInventory(
                response?.data?.data || []
            );

        } catch (err) {

            console.error(
                "Inventory Error:",
                err
            );

            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Unable to load inventory."
            );

        } finally {

            setLoading(false);

        }

    };


    // =====================================================
    // INITIAL LOAD
    // =====================================================

    useEffect(() => {

        loadInventory();

    }, []);


    // =====================================================
    // FORMAT NUMBER
    // =====================================================

    const formatNumber = (
        value
    ) => {

        return Number(
            value || 0
        ).toLocaleString(
            "en-IN",
            {
                maximumFractionDigits: 2
            }
        );

    };


    // =====================================================
    // FORMAT TONS
    // =====================================================

    const formatTons = (
        kg
    ) => {

        return (
            Number(
                kg || 0
            ) / 1000
        ).toLocaleString(
            "en-IN",
            {
                maximumFractionDigits: 2
            }
        );

    };


    // =====================================================
    // CHAMBER DISPLAY NAME
    //
    // DATABASE:
    // C-01 -> A
    // C-02 -> B
    // C-03 -> C
    // C-04 -> D
    //
    // DATABASE VALUES ARE NOT CHANGED.
    // =====================================================

    const getChamberDisplayName = (
        chamber
    ) => {

        const chamberMap = {

            "C-01": "A",

            "C-02": "B",

            "C-03": "C",

            "C-04": "D"

        };


        const normalized =
            String(
                chamber || ""
            )
                .trim()
                .toUpperCase();


        return (
            chamberMap[
                normalized
            ] ||
            chamber ||
            "-"
        );

    };


    // =====================================================
    // NORMALIZE INVENTORY
    // =====================================================

    const normalizedInventory =
        useMemo(() => {

            return inventory.map(
                (item) => {

                    const chamberNo =
                        item.chamberNo ??
                        item.chamber_no ??
                        "-";


                    return {

                        ...item,

                        id:
                            item.id ??
                            item.inventoryId ??
                            item.inventory_id,

                        chamberNo:

                            chamberNo,

                        floorNo:
                            item.floor_no ?? item.floorNo ?? null,

                        lotNo:
                            item.lotNo ??
                            item.lot_no ??
                            "-",

                        customerName:
                            item.customerName ??
                            item.customer_name ??
                            "-",

                        productMark:
                            item.productMark ??
                            item.product_mark ??
                            "-",

                        receivedBags:
                            Number(
                                item.receivedBags ??
                                item.received_bags ??
                                0
                            ),

                        deliveredBags:
                            Number(
                                item.deliveredBags ??
                                item.delivered_bags ??
                                0
                            ),

                        remainingBags:
                            Number(
                                item.remainingBags ??
                                item.remaining_bags ??
                                0
                            ),

                        receivedWeightKg:
                            Number(
                                item.receivedWeightKg ??
                                item.received_weight_kg ??
                                0
                            ),

                        deliveredWeightKg:
                            Number(
                                item.deliveredWeightKg ??
                                item.delivered_weight_kg ??
                                0
                            ),

                        remainingWeightKg:
                            Number(
                                item.remainingWeightKg ??
                                item.remaining_weight_kg ??
                                0
                            ),

                        status:
                            item.status ||
                            "ACTIVE",

                        location:
                            item.location ||
                            ""

                    };

                }
            );

        }, [inventory]);


    // =====================================================
    // CHAMBERS
    // =====================================================

    const chambers =
        useMemo(() => {

            const values =
                normalizedInventory
                    .map(
                        (item) =>
                            item.chamberNo
                    )
                    .filter(
                        (value) =>
                            value &&
                            value !== "-"
                    );


            return [
                ...new Set(
                    values
                )
            ];

        }, [
            normalizedInventory
        ]);


    // =====================================================
    // FILTER INVENTORY
    // =====================================================

    const filteredInventory =
        useMemo(() => {

            const text =
                search
                    .trim()
                    .toLowerCase();


            return normalizedInventory.filter(
                (item) => {

                    const chamberDisplay =
                        getChamberDisplayName(
                            item.chamberNo
                        );


                    const matchesSearch =

                        !text ||

                        String(
                            item.chamberNo
                        )
                            .toLowerCase()
                            .includes(text) ||

                        String(
                            chamberDisplay
                        )
                            .toLowerCase()
                            .includes(text) ||

                        String(
                            item.lotNo
                        )
                            .toLowerCase()
                            .includes(text) ||

                        String(
                            item.customerName
                        )
                            .toLowerCase()
                            .includes(text) ||

                        String(
                            item.productMark
                        )
                            .toLowerCase()
                            .includes(text) ||

                        String(
                            item.location
                        )
                            .toLowerCase()
                            .includes(text);


                    const matchesStatus =

                        statusFilter ===
                            "ALL" ||

                        item.status ===
                            statusFilter;


                    const matchesChamber =

                        chamberFilter ===
                            "ALL" ||

                        item.chamberNo ===
                            chamberFilter;


                    return (
                        matchesSearch &&
                        matchesStatus &&
                        matchesChamber
                    );

                }
            );

        }, [
            normalizedInventory,
            search,
            statusFilter,
            chamberFilter
        ]);


    // =====================================================
    // SUMMARY
    // =====================================================

    const summary =
        useMemo(() => {

            return normalizedInventory.reduce(

                (total, item) => {

                    total.lots += 1;

                    total.receivedBags +=
                        item.receivedBags;

                    total.deliveredBags +=
                        item.deliveredBags;

                    total.remainingBags +=
                        item.remainingBags;

                    total.remainingWeightKg +=
                        item.remainingWeightKg;

                    return total;

                },

                {
                    lots: 0,
                    receivedBags: 0,
                    deliveredBags: 0,
                    remainingBags: 0,
                    remainingWeightKg: 0
                }

            );

        }, [
            normalizedInventory
        ]);


    // =====================================================
    // EXPORT TO EXCEL
    // =====================================================

    const exportToExcel = () => {

        if (
            filteredInventory.length === 0
        ) {

            alert(
                "There is no inventory data to export."
            );

            return;

        }


        // =================================================
        // EXCEL DATA
        // =================================================

        const excelData =
            filteredInventory.map(
                (
                    item,
                    index
                ) => ({

                    "Sr. No.":

                        index + 1,


                    "Chamber":

                        getChamberDisplayName(
                            item.chamberNo
                        ),


                    "Lot No.":

                        item.lotNo,


                    "Customer / M/s":

                        item.customerName,


                    "Product Mark":

                        item.productMark,


                    "Location":

                        item.location ||
                        "-",


                    "Received Bags":

                        item.receivedBags,


                    "Delivered Bags":

                        item.deliveredBags,


                    "Remaining Bags":

                        item.remainingBags,


                    "Received Weight (KG)":

                        item.receivedWeightKg,


                    "Delivered Weight (KG)":

                        item.deliveredWeightKg,


                    "Remaining Weight (KG)":

                        item.remainingWeightKg,


                    "Remaining Weight (Tons)":

                        Number(
                            item.remainingWeightKg ||
                            0
                        ) / 1000,


                    "Status":

                        item.status ===
                        "PARTIALLY_DELIVERED"

                            ? "Partial"

                            :

                        item.status ===
                        "CLOSED"

                            ? "Closed"

                            :

                        "Active"

                })
            );


        // =================================================
        // CREATE WORKSHEET
        // =================================================

        const worksheet =
            XLSX.utils.json_to_sheet(
                excelData
            );


        // =================================================
        // COLUMN WIDTHS
        // =================================================

        worksheet["!cols"] = [

            {
                wch: 8
            },

            {
                wch: 12
            },

            {
                wch: 14
            },

            {
                wch: 25
            },

            {
                wch: 18
            },

            {
                wch: 18
            },

            {
                wch: 16
            },

            {
                wch: 17
            },

            {
                wch: 17
            },

            {
                wch: 22
            },

            {
                wch: 23
            },

            {
                wch: 24
            },

            {
                wch: 25
            },

            {
                wch: 18
            }

        ];


        // =================================================
        // CREATE WORKBOOK
        // =================================================

        const workbook =
            XLSX.utils.book_new();


        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Inventory"
        );


        // =================================================
        // DATE
        // =================================================

        const today =
            new Date();


        const dateString =
            today
                .toLocaleDateString(
                    "en-IN"
                )
                .replace(
                    /\//g,
                    "-"
                );


        // =================================================
        // FILE NAME
        // =================================================

        const fileName =
            `Bhimashankar_Agro_Inventory_${dateString}.xlsx`;


        // =================================================
        // DOWNLOAD
        // =================================================

        XLSX.writeFile(
            workbook,
            fileName
        );

    };


    // =====================================================
    // VIEW RECEIPT
    // =====================================================

    const viewReceipt = (
        item
    ) => {

        const grnId =
            item.grnId ??
            item.grn_id;


        if (!grnId) {

            alert(
                "GRN information is not available for this inventory record."
            );

            return;

        }


        navigate(
            `/goods-received/print/${grnId}`
        );

    };


    // =====================================================
    // EDIT RECEIPT
    // =====================================================

    const editReceipt = (
        item
    ) => {

        const grnId =
            item.grnId ??
            item.grn_id;


        if (!grnId) {

            alert(
                "GRN information is not available for this inventory record."
            );

            return;

        }


        navigate(
            `/goods-received?edit=${grnId}`
        );

    };


    // =====================================================
    // PRINT RECEIPT
    // =====================================================

    const printReceipt = (
        item
    ) => {

        const grnId =
            item.grnId ??
            item.grn_id;


        if (!grnId) {

            alert(
                "GRN information is not available for this inventory record."
            );

            return;

        }


        navigate(
            `/goods-received/print/${grnId}`
        );

    };


    // =====================================================
    // OPEN DELETE MODAL
    // =====================================================

    const openDeleteModal = (
        item
    ) => {

        setDeleteItem(
            item
        );

    };


    // =====================================================
    // DELETE GRN
    // =====================================================

    const confirmDelete =
        async () => {

            if (
                !deleteItem
            ) {

                return;

            }


            const grnId =
                deleteItem.grnId ??
                deleteItem.grn_id;


            if (!grnId) {

                alert(
                    "GRN information is not available."
                );

                return;

            }


            try {

                setDeleting(
                    true
                );


                await deleteGRN(
                    grnId
                );


                setDeleteItem(
                    null
                );


                await loadInventory();

            } catch (err) {

                console.error(
                    "Delete GRN Error:",
                    err
                );


                alert(
                    err?.response?.data?.message ||
                    err?.message ||
                    "Unable to delete this receipt."
                );

            } finally {

                setDeleting(
                    false
                );

            }

        };


    // =====================================================
    // STATUS CLASS
    // =====================================================

    const getStatusClass = (
        status
    ) => {

        switch (
            status
        ) {

            case "ACTIVE":

                return (
                    "inventory-status active"
                );


            case "PARTIALLY_DELIVERED":

                return (
                    "inventory-status partial"
                );


            case "CLOSED":

                return (
                    "inventory-status closed"
                );


            default:

                return (
                    "inventory-status"
                );

        }

    };


    // =====================================================
    // RENDER
    // =====================================================

    return (

        <div className="inventory-page">


            {/* =================================================
                HEADER
            ================================================= */}

            <div className="inventory-header">

                <div>

                    <div className="inventory-title-row">

                        <div className="inventory-title-icon">

                            <Package
                                size={22}
                            />

                        </div>


                        <div>

                            <h1>
                                Inventory
                            </h1>

                            <p>
                                Manage stored potato stock,
                                lots and receipts
                            </p>

                        </div>

                    </div>

                </div>


                {/* =================================================
                    HEADER ACTIONS
                ================================================= */}

                <div className="inventory-header-actions">


                    {/* EXPORT EXCEL */}

                    <button
                        className="inventory-export"
                        onClick={
                            exportToExcel
                        }
                        disabled={
                            loading ||
                            filteredInventory.length === 0
                        }
                        title="Export inventory to Excel"
                    >

                        <FileSpreadsheet
                            size={16}
                        />

                        Export Excel

                    </button>


                    {/* REFRESH */}

                    <button
                        className="inventory-refresh"
                        onClick={
                            loadInventory
                        }
                        disabled={
                            loading
                        }
                    >

                        <RefreshCw
                            size={16}
                            className={
                                loading
                                    ? "inventory-spin"
                                    : ""
                            }
                        />

                        Refresh

                    </button>

                </div>

            </div>


            {/* =================================================
                SUMMARY
            ================================================= */}

            <div className="inventory-summary">


                {/* ACTIVE LOTS */}

                <div className="inventory-summary-card">

                    <div className="inventory-summary-icon blue">

                        <Package
                            size={20}
                        />

                    </div>


                    <div>

                        <span>
                            Active Lots
                        </span>

                        <strong>

                            {
                                formatNumber(
                                    summary.lots
                                )
                            }

                        </strong>

                    </div>

                </div>


                {/* RECEIVED */}

                <div className="inventory-summary-card">

                    <div className="inventory-summary-icon green">

                        <Boxes
                            size={20}
                        />

                    </div>


                    <div>

                        <span>
                            Received Bags
                        </span>

                        <strong>

                            {
                                formatNumber(
                                    summary.receivedBags
                                )
                            }

                        </strong>

                    </div>

                </div>


                {/* REMAINING BAGS */}

                <div className="inventory-summary-card">

                    <div className="inventory-summary-icon purple">

                        <Package
                            size={20}
                        />

                    </div>


                    <div>

                        <span>
                            Remaining Bags
                        </span>

                        <strong>

                            {
                                formatNumber(
                                    summary.remainingBags
                                )
                            }

                        </strong>

                    </div>

                </div>


                {/* REMAINING WEIGHT */}

                <div className="inventory-summary-card">

                    <div className="inventory-summary-icon orange">

                        <Weight
                            size={20}
                        />

                    </div>


                    <div>

                        <span>
                            Remaining Weight
                        </span>

                        <strong>

                            {
                                formatTons(
                                    summary.remainingWeightKg
                                )
                            }

                            <small>
                                {" "}Tons
                            </small>

                        </strong>

                    </div>

                </div>

            </div>


            {/* =================================================
                FILTERS
            ================================================= */}

            <div className="inventory-filter-card">


                {/* SEARCH */}

                <div className="inventory-search">

                    <Search
                        size={17}
                    />


                    <input
                        type="text"
                        placeholder="Search chamber, lot, customer, product mark..."
                        value={
                            search
                        }
                        onChange={
                            (e) =>
                                setSearch(
                                    e.target.value
                                )
                        }
                    />


                    <button
                        type="button"
                        className={`register-voice-button ${registerListening ? "is-listening" : ""}`}
                        onClick={startRegisterVoiceSearch}
                        title="Marathi voice search"
                        aria-label="Marathi voice search"
                    >
                        <Mic size={17} />
                    </button>

                    {search && (

                        <button
                            type="button"
                            onClick={() =>
                                setSearch("")
                            }
                            className="clear-search"
                            title="Clear search"
                        >

                            <X
                                size={15}
                            />

                        </button>

                    )}

                </div>


                {/* CHAMBER */}

                <select
                    value={
                        chamberFilter
                    }
                    onChange={
                        (e) =>
                            setChamberFilter(
                                e.target.value
                            )
                    }
                >

                    <option value="ALL">
                        All Chambers
                    </option>


                    {chambers.map(
                        (
                            chamber
                        ) => (

                            <option
                                key={
                                    chamber
                                }
                                value={
                                    chamber
                                }
                            >

                                {
                                    getChamberDisplayName(
                                        chamber
                                    )
                                }

                            </option>

                        )
                    )}

                </select>


                {/* STATUS */}

                <select
                    value={
                        statusFilter
                    }
                    onChange={
                        (e) =>
                            setStatusFilter(
                                e.target.value
                            )
                    }
                >

                    <option value="ALL">
                        All Status
                    </option>

                    <option value="ACTIVE">
                        Active
                    </option>

                    <option value="PARTIALLY_DELIVERED">
                        Partially Delivered
                    </option>

                    <option value="CLOSED">
                        Closed
                    </option>

                </select>

            </div>


            {/* =================================================
                ERROR
            ================================================= */}

            {error && (

                <div className="inventory-error">

                    <AlertTriangle
                        size={18}
                    />

                    <span>
                        {error}
                    </span>


                    <button
                        type="button"
                        onClick={
                            loadInventory
                        }
                    >
                        Retry
                    </button>

                </div>

            )}


            {/* =================================================
                CURRENT STOCK
            ================================================= */}

            <div className="inventory-table-card">


                <div className="inventory-table-header">

                    <div>

                        <h3>
                            Current Stock
                        </h3>

                        <p>

                            {
                                filteredInventory.length
                            }

                            {" "}records found

                        </p>

                    </div>

                </div>


                {/* LOADING */}

                {loading ? (

                    <div className="inventory-loading">

                        <RefreshCw
                            size={22}
                            className="inventory-spin"
                        />

                        <span>
                            Loading inventory...
                        </span>

                    </div>

                ) : filteredInventory.length === 0 ? (

                    <div className="inventory-empty">

                        <Package
                            size={42}
                        />

                        <h3>
                            No inventory found
                        </h3>

                        <p>
                            Try changing your search
                            or filter.
                        </p>

                    </div>

                ) : (

                    <div className="inventory-table-wrapper">

                        <table className="inventory-table">

                            <thead>

                                <tr>

                                    <th>
                                        CHAMBER
                                    </th>

                                    <th>
                                        LOT NO.
                                    </th>

                                    <th>
                                        CUSTOMER
                                    </th>

                                    <th>
                                        PRODUCT MARK
                                    </th>

                                    <th>
                                        RECEIVED
                                    </th>

                                    <th>
                                        DELIVERED
                                    </th>

                                    <th>
                                        REMAINING
                                    </th>

                                    <th>
                                        WEIGHT
                                    </th>

                                    <th>
                                        STATUS
                                    </th>

                                    <th className="action-column">
                                        ACTIONS
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {filteredInventory.map(
                                    (
                                        item
                                    ) => (

                                        <tr
                                            key={
                                                item.id
                                            }
                                        >


                                            {/* CHAMBER */}

                                            <td>

                                                <div className="chamber-cell">

                                                    <div className="chamber-icon">

                                                        <MapPin
                                                            size={14}
                                                        />

                                                    </div>


                                                    <strong>

                                                        {
                                                            (() => {
                                                                const chamberLabel =
                                                                    getChamberDisplayName(item.chamberNo);
                                                                const floor = item.floorNo;

                                                                return floor !== null &&
                                                                    floor !== undefined &&
                                                                    String(floor).trim() !== ""
                                                                    ? `${chamberLabel}-${floor}`
                                                                    : chamberLabel;
                                                            })()
                                                        }

                                                    </strong>

                                                </div>

                                            </td>


                                            {/* LOT */}

                                            <td>

                                                <span className="lot-number">

                                                    {
                                                        item.lotNo
                                                    }

                                                </span>

                                            </td>


                                            {/* CUSTOMER */}

                                            <td>

                                                <div className="customer-cell">

                                                    <User
                                                        size={14}
                                                    />

                                                    <span>

                                                        {
                                                            item.customerName
                                                        }

                                                    </span>

                                                </div>

                                            </td>


                                            {/* PRODUCT MARK */}

                                            <td>

                                                <span className="product-mark">

                                                    {
                                                        item.productMark
                                                    }

                                                </span>

                                            </td>


                                            {/* RECEIVED */}

                                            <td>

                                                <div className="stock-number">

                                                    <strong>

                                                        {
                                                            formatNumber(
                                                                item.receivedBags
                                                            )
                                                        }

                                                    </strong>

                                                    <small>
                                                        Bags
                                                    </small>

                                                </div>

                                            </td>


                                            {/* DELIVERED */}

                                            <td>

                                                <div className="stock-number delivered">

                                                    <strong>

                                                        {
                                                            formatNumber(
                                                                item.deliveredBags
                                                            )
                                                        }

                                                    </strong>

                                                    <small>
                                                        Bags
                                                    </small>

                                                </div>

                                            </td>


                                            {/* REMAINING */}

                                            <td>

                                                <div className="stock-number remaining">

                                                    <strong>

                                                        {
                                                            formatNumber(
                                                                item.remainingBags
                                                            )
                                                        }

                                                    </strong>

                                                    <small>
                                                        Bags
                                                    </small>

                                                </div>

                                            </td>


                                            {/* WEIGHT */}

                                            <td>

                                                <div className="weight-cell">

                                                    <strong>

                                                        {
                                                            formatTons(
                                                                item.remainingWeightKg
                                                            )
                                                        }

                                                    </strong>

                                                    <small>
                                                        Tons
                                                    </small>

                                                </div>

                                            </td>


                                            {/* STATUS */}

                                            <td>

                                                <span
                                                    className={
                                                        getStatusClass(
                                                            item.status
                                                        )
                                                    }
                                                >

                                                    <span className="status-dot"></span>


                                                    {
                                                        item.status ===
                                                        "PARTIALLY_DELIVERED"

                                                            ? "Partial"

                                                            :

                                                        item.status ===
                                                        "CLOSED"

                                                            ? "Closed"

                                                            :

                                                        "Active"
                                                    }

                                                </span>

                                            </td>


                                            {/* ACTIONS */}

                                            <td>

                                                <div className="inventory-actions">


                                                    {/* VIEW */}

                                                    <button
                                                        type="button"
                                                        className="inventory-action view"
                                                        title="View Receipt"
                                                        onClick={() =>
                                                            viewReceipt(
                                                                item
                                                            )
                                                        }
                                                    >

                                                        <Eye
                                                            size={15}
                                                        />

                                                    </button>


                                                    {/* EDIT */}

                                                    <button
                                                        type="button"
                                                        className="inventory-action edit"
                                                        title="Edit Receipt"
                                                        onClick={() =>
                                                            editReceipt(
                                                                item
                                                            )
                                                        }
                                                    >

                                                        <Pencil
                                                            size={15}
                                                        />

                                                    </button>


                                                    {/* PRINT */}

                                                    <button
                                                        type="button"
                                                        className="inventory-action print"
                                                        title="Print Receipt"
                                                        onClick={() =>
                                                            printReceipt(
                                                                item
                                                            )
                                                        }
                                                    >

                                                        <Printer
                                                            size={15}
                                                        />

                                                    </button>


                                                    {/* DELETE */}

                                                    <button
                                                        type="button"
                                                        className="inventory-action delete"
                                                        title="Delete Receipt"
                                                        onClick={() =>
                                                            openDeleteModal(
                                                                item
                                                            )
                                                        }
                                                    >

                                                        <Trash2
                                                            size={15}
                                                        />

                                                    </button>

                                                </div>

                                            </td>

                                        </tr>

                                    )
                                )}

                            </tbody>

                        </table>

                    </div>

                )}

            </div>


            {/* =================================================
                DELETE MODAL
            ================================================= */}

            {deleteItem && (

                <div
                    className="inventory-modal-overlay"
                    onClick={() =>
                        setDeleteItem(
                            null
                        )
                    }
                >

                    <div
                        className="inventory-delete-modal"
                        onClick={
                            (e) =>
                                e.stopPropagation()
                        }
                    >


                        <div className="delete-icon">

                            <AlertTriangle
                                size={25}
                            />

                        </div>


                        <h2>
                            Delete Receipt?
                        </h2>


                        <p>

                            Are you sure you want to
                            delete GRN{" "}

                            <strong>

                                {
                                    deleteItem.grnNo ||
                                    deleteItem.grn_no ||
                                    "receipt"
                                }

                            </strong>

                            ?

                        </p>


                        <div className="delete-warning">

                            This action will remove the
                            receipt and its related
                            inventory records.

                        </div>


                        <div className="delete-modal-actions">


                            <button
                                type="button"
                                className="cancel-delete"
                                onClick={() =>
                                    setDeleteItem(
                                        null
                                    )
                                }
                                disabled={
                                    deleting
                                }
                            >

                                Cancel

                            </button>


                            <button
                                type="button"
                                className="confirm-delete"
                                onClick={
                                    confirmDelete
                                }
                                disabled={
                                    deleting
                                }
                            >

                                <Trash2
                                    size={16}
                                />

                                {
                                    deleting
                                        ? "Deleting..."
                                        : "Delete Receipt"
                                }

                            </button>

                        </div>

                    </div>

                </div>

            )}

        </div>

    );

};


export default Inventory;